module Expr
  ( ApplyF
  , LambdaF
  , ExprF(..)
  , Expr(..)
  , Application
  , Abstraction
  , generator
  , expression
  , toLexemes
  , print
  , checkPrintParse
  , ParserQuery(..)
  , parserComponent
  , render
  ) where

import Prelude hiding (between, div)

import Control.Lazy (fix)
import Control.Monad.Gen (class MonadGen, sized)
import Control.Monad.Gen as Gen
import Control.Monad.Rec.Class (class MonadRec)
import Custom.Html (overlay, unfoldHTML)
import Data.Array ((:))
import Data.Array.NonEmpty (NonEmptyArray, cons', foldl1, snoc, some)
import Data.Array.NonEmpty as NonEmptyArray
import Data.Either (Either(..))
import Data.Eq (class Eq1)
import Data.Foldable (class Foldable, oneOf)
import Data.Generic.Rep (class Generic)
import Data.Int (quot)
import Data.List (List)
import Data.Maybe (Maybe(..), maybe)
import Data.Newtype (class Newtype, over)
import Data.Show.Generic (genericShow)
import Data.String.Gen (genAlphaString)
import Data.Traversable (class Traversable)
import Data.Tuple (Tuple(..), uncurry)
import Halogen (ClassName(..), HalogenM, mkEval, modify_, put)
import Halogen.Component (Component, mkComponent)
import Halogen.HTML (HTML, div, span, text)
import Halogen.HTML.Core (widget)
import Halogen.HTML.Properties (class_)
import Lexeme (Lexeme(..))
import Lexeme as Lexeme
import Matryoshka (class Corecursive, class Recursive, anaM, cata)
import Parsing (ParseError, Parser, fail, runParser)
import Parsing.Combinators (between, optionMaybe, try, notFollowedBy)
import Parsing.Token (token)
import Token (Token(..), tokens)

type ApplyF r = { function :: r, argument :: r }
type LambdaF r = { binder :: String, body :: r }
data ExprF r
  = ApplyF (ApplyF r)
  | LambdaF (LambdaF r)
  | VariableF String

derive instance Generic (ExprF r) _
derive instance Eq1 ExprF
derive instance Eq r => Eq (ExprF r)
derive instance Functor ExprF
derive instance Foldable ExprF
derive instance Traversable ExprF

instance Show r => Show (ExprF r) where
  show e = genericShow e

data Expr
  = Application Application
  | Abstraction Abstraction
  | Variable String

type Application = { function :: Expr, argument :: Expr }
type Abstraction = { binder :: String, body :: Expr }

derive instance Generic Expr _
derive instance Eq Expr

instance Show Expr where
  show e = genericShow e

instance Recursive Expr ExprF where
  project = case _ of
    Application a -> ApplyF a
    Abstraction a -> LambdaF a
    Variable x -> VariableF x

instance Corecursive Expr ExprF where
  embed = case _ of
    ApplyF a -> Application a
    LambdaF l -> Abstraction l
    VariableF x -> Variable x

generator :: forall g. MonadRec g => MonadGen g => g Expr
generator = sized (anaM coalg)
  where
  variable = VariableF <$> genAlphaString
  coalg sz =
    if sz <= 0 then variable else Gen.oneOf if sz <= 1 then small else large
    where
    abstraction = ado
      binder <- genAlphaString
      in LambdaF { binder, body: max 0 (sz - 1) }
    small = cons' abstraction [ variable ]
    half = quot sz 2
    large =
      NonEmptyArray.cons
        (pure $ ApplyF { function: half, argument: half })
        small

nextToken :: Parser (List Token) Token
nextToken = token (\(Token t) -> t.end)

expr :: Parser (List Token) Expr -> Parser (List Token) Expr
expr e = oneOf
  [ abs
  , app
  , fail "Expected lambda, application, or identifier"
  ]
  where
  abs = ado
    lambda
    binder <- identifier
    dot
    body <- e
    in Abstraction { binder, body }
  app = do
    fs <- some (try atom)
    let i = foldl1 (\function argument -> Application { function, argument }) fs
    t <- optionMaybe (try e)
    pure $ case t of
      Nothing -> i
      Just l -> Application { function: i, argument: l }
  atom = oneOf
    [ Variable <$> identifier
    , between openParen closeParen e
    , fail "Expected an atom (identifier or parenthesized expression)"
    ]
  lexeme l = try $ do
    Token t <- nextToken
    when (t.lexeme /= l) <<< fail $ "Expected " <> show l
  lambda = lexeme Lambda
  dot = lexeme Dot
  openParen = lexeme OpenParen
  closeParen = lexeme CloseParen
  identifier = try $ do
    Token t <- nextToken
    case t.lexeme of
      Identifier id -> pure id
      _ -> fail "Expected an identifier"

expression :: Parser (List Token) Expr
expression = fix expr

toLexemes :: Expr -> NonEmptyArray Lexeme
toLexemes e = cata coalg e $ Tuple true true
  where
  p arr = NonEmptyArray.cons OpenParen $ snoc arr CloseParen
  apply a leading trailing =
    if leading then f <> a.argument (Tuple false trailing)
    else p (f <> a.argument (Tuple false true))
    where
    f = a.function $ Tuple true false
  lambda l = const (if _ then arr else p arr)
    where
    arr =
      NonEmptyArray.cons Lambda
        $ NonEmptyArray.cons (Identifier l.binder)
        $ NonEmptyArray.cons Dot
        $ l.body (Tuple true true)
  coalg = case _ of
    ApplyF a -> uncurry (apply a)
    LambdaF l -> uncurry (lambda l)
    VariableF x -> const $ cons' (Identifier x) []

print :: Expr -> String
print = Lexeme.printMany <<< toLexemes

checkPrintParse :: Expr -> Boolean
checkPrintParse x = case rt of
  Left _ -> false
  Right r -> r
  where
  rt = do
    toks <- runParser (print x) (tokens Lexeme.parser)
    y <- runParser toks expression
    pure $ x == y

data ParserQuery a = Parse (List Token) (Expr -> a)

derive instance Generic (ParserQuery a) _

newtype ParserComponentState = ParserComponentState
  { parseError :: Maybe ParseError, lastExpression :: Maybe Expr }

derive instance ntParserComponentState :: Newtype ParserComponentState _

parseQuery ::
  forall a action slots output m.
  ParserQuery a ->
  HalogenM ParserComponentState action slots output m (Maybe a)
parseQuery (Parse toks onParse) = do
  case runParser toks (expression <* notFollowedBy nextToken) of
    Left pe -> do
      modify_ (over ParserComponentState (_ { parseError = Just pe }))
      pure Nothing
    Right ex -> do
      put $ ParserComponentState { parseError: Nothing, lastExpression: Just ex }
      pure $ Just $ onParse ex

parserComponent :: forall input output m. Component ParserQuery input output m
parserComponent = mkComponent
  { initialState: const $ ParserComponentState
      { parseError: Nothing, lastExpression: Nothing }
  , render: renderComponent
  , eval: mkEval
      { initialize: Nothing
      , receive: const Nothing
      , handleAction: const (pure unit)
      , handleQuery: parseQuery
      , finalize: Nothing
      }
  }
  where
  renderComponent (ParserComponentState st) =
    div
      [ class_ $ ClassName "parser" ]
      $
        maybe []
          (\pe -> pure $ overlay [ ClassName "parser_error" ] [ text (show pe) ])
          st.parseError
          <> maybe [] (\ex -> pure $ render ex) st.lastExpression

render :: forall slot action. Expr -> HTML slot action
render = unfoldHTML coalg
  where
  multiApp = case _ of
    Application a -> multiApp a.function <<< (a.argument : _)
    h -> (h : _)
  coalg = case _ of
    Application a ->
      div
        [ class_ $ ClassName "expr_application" ]
        [ span [ class_ $ ClassName "expr_application_root" ] [ text "@" ]
        , div
            [ class_ $ ClassName "expr_application_leaves" ]
            $ map (widget <<< Left)
            $ multiApp a.function [ a.argument ]
        ]
    Abstraction a ->
      div
        [ class_ $ ClassName "expr_abstraction" ]
        [ span
            [ class_ $ ClassName "expr_abstraction_root" ]
            [ text "\x03bb ", text a.binder ]
        , div
            [ class_ $ ClassName "expr_abstraction_leaves" ]
            $ map (widget <<< Left)
            $ multiApp a.body []
        ]
    Variable x -> div [ class_ $ ClassName "expr_variable" ] [ text x ]
